import { getSubunitRatio, moneyFormatSubunit } from "@oysterhr/common"
import type { TFunction } from "react-i18next"
import {
  Money,
  MoneyInput,
  MonthsEnum,
  PaymentFrequencyEnum,
  VariableCompensation,
} from "@oysterhr/schema"
import { DateTime } from "luxon"
import { VariableCompensationSummaryProps } from "./VariableCompensationSummary"

export const defaultMonthsForFrequency = (frequency?: PaymentFrequencyEnum) => {
  switch (frequency) {
    case PaymentFrequencyEnum.Monthly:
      return [
        MonthsEnum.January,
        MonthsEnum.February,
        MonthsEnum.March,
        MonthsEnum.April,
        MonthsEnum.May,
        MonthsEnum.June,
        MonthsEnum.July,
        MonthsEnum.August,
        MonthsEnum.September,
        MonthsEnum.October,
        MonthsEnum.November,
        MonthsEnum.December,
      ]
    case PaymentFrequencyEnum.Quarterly:
      return [MonthsEnum.March, MonthsEnum.June, MonthsEnum.September, MonthsEnum.December]
    case PaymentFrequencyEnum.Biannual:
      return [MonthsEnum.June, MonthsEnum.December]
    case PaymentFrequencyEnum.Yearly:
      return [MonthsEnum.December]
    default:
      return []
  }
}

export const getMonths = (frequency?: PaymentFrequencyEnum, selectedMonths?: MonthsEnum[]) => {
  // We only have selected months as a feature for yearly selections (but the data may still be there in the form)
  // So we explicitly descriminate the usage here
  if (frequency === PaymentFrequencyEnum.Yearly) {
    return selectedMonths || []
  }
  return defaultMonthsForFrequency(frequency)
}

export const formatMoney = (money: Money) => {
  if (!money?.subunit) {
    return `${money?.currencyCode} -`
  }

  return moneyFormatSubunit(money.subunit, money.currencyCode, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

export const paymentsCount = (
  t: TFunction,
  frequency: PaymentFrequencyEnum,
  selectedMonths?: MonthsEnum[],
) => {
  const months = getMonths(frequency, selectedMonths)
  const count = months.length

  return t("GENERIC.summary.paymentCount", {
    count,
    monthsCount: t(`GENERIC.summary.monthsCount.${count}`),
  })
}

export const paidInMonths = (t: TFunction, varCompSummary: VariableCompensationSummaryProps) => {
  const { frequency, selectedMonths, effectiveDate, startDate } = varCompSummary
  const months = getMonths(frequency, selectedMonths)
  if (months.length === 0) {
    return ""
  }
  const monthsStrings = months.map(month =>
    t(`months.long.${month.toLowerCase()}`, { ns: "Shared" }),
  )

  if (months.length === 12) {
    if (!effectiveDate || !startDate) {
      return t("GENERIC.summary.startingInFirstMonth", { ns: "variable_compensation/Common" })
    }

    if (
      // Var. comp. effective date is equal to the engagement start date
      DateTime.fromISO(effectiveDate)
        .startOf("month")
        .equals(DateTime.fromISO(startDate).startOf("month"))
    ) {
      // "Starting in the first month of employment"
      return t("GENERIC.summary.startingInFirstMonth", { ns: "variable_compensation/Common" })
    } else {
      /* Var. comp. is changed/added while the employment is in the "Engaged" state:
        show the scheduled date from when the var. comp. will be effective. */
      return t("GENERIC.summary.startingFromDate", {
        ns: "variable_compensation/Common",
        date: DateTime.fromISO(effectiveDate).toLocaleString({
          ...DateTime.DATE_FULL,
          day: undefined,
        }),
      })
    }
  } else {
    const monthsJoined =
      monthsStrings.length > 1
        ? monthsStrings.slice(0, -1).join(", ") + " and " + monthsStrings[monthsStrings.length - 1]
        : monthsStrings[0]
    return t("GENERIC.summary.paidInMonths", {
      months: monthsJoined,
      ns: "variable_compensation/Common",
    })
  }
}

export const calculateAmount = (
  amount: Money,
  frequency?: PaymentFrequencyEnum,
  selectedMonths?: MonthsEnum[],
) => {
  const months = getMonths(frequency, selectedMonths)
  const monthsCount = months.length || 1

  const perPeriodAmountSubunit =
    frequency === PaymentFrequencyEnum.Yearly ? amount.subunit / monthsCount : amount.subunit
  const annualAmountSubunit =
    frequency === PaymentFrequencyEnum.Yearly ? amount.subunit : amount.subunit * monthsCount

  return {
    annualAmount: {
      ...amount,
      subunit: annualAmountSubunit,
    },
    perPeriodAmount: {
      ...amount,
      subunit: perPeriodAmountSubunit,
    },
    months,
  }
}

export const getAmountFromPercentageOfGross = (
  salary: Money,
  percentage: number,
  frequency: PaymentFrequencyEnum,
  selectedMonths?: MonthsEnum[],
) => {
  const subunit = (salary.subunit * percentage) / 100
  return calculateAmount({ ...salary, subunit }, frequency, selectedMonths)
}

/** @todo deprecate this
 * Use buildMoney from @oysterhr/common instead **/
export const buildMoney = (input?: Partial<MoneyInput>) => {
  const currencyCode = input?.currencyCode || "USD"
  const subunit = input?.subunit || 0
  const subunitToUnit = getSubunitRatio(currencyCode)
  const formatted = ""
  return {
    currencyCode,
    subunit,
    subunitToUnit,
    formatted,
  } as Money
}

/**
 *
 * @param compensation - the variable compensation object
 * @param salary - the salary represented as Money object
 * @returns variable compensation amount as subunit
 */
export const getVariableCompensationValue = (compensation: VariableCompensation, salary: Money) => {
  let value = compensation.amount?.subunit
  if (compensation.amountAsPercentage) {
    const { annualAmount } = getAmountFromPercentageOfGross(
      salary,
      compensation.amountAsPercentage,
      compensation.paymentFrequency,
      compensation.selectedMonths,
    )
    value = annualAmount.subunit
  } else if (compensation.amount && compensation.paymentFrequency !== PaymentFrequencyEnum.Yearly) {
    const amount = calculateAmount(
      compensation.amount,
      compensation.paymentFrequency,
      compensation.selectedMonths,
    )
    value = amount.annualAmount.subunit
  }
  return value
}
